import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Eye, Paperclip, Pencil, Trash2 } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { DatosParaPagar } from "@/componentes/catalogos/DatosParaPagar"
import { FormularioArticulo } from "@/componentes/inventario/FormularioArticulo"
import { BloquePago } from "@/componentes/ordenes/BloquePago"
import { ClasificarLinea } from "@/componentes/ordenes/ClasificarLinea"
import { ConceptoIslr } from "@/componentes/ordenes/ConceptoIslr"
import { DatosOrden } from "@/componentes/ordenes/DatosOrden"
import { ModalEjecutarPago } from "@/componentes/ordenes/ModalEjecutarPago"
import { InsigniaNaturaleza } from "@/componentes/ordenes/naturaleza"
import type { Linea, Orden } from "@/componentes/ordenes/tipos"
import { ESTADOS, TONO_ESTADO } from "@/componentes/ordenes/tipos"
import { comoCantidad, paraEditar } from "@/componentes/tesoreria/dinero"
import {
  type Adjuntando,
  AdjuntosNuevos,
  archivosPegados,
  sumar,
} from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"
import { Cajon } from "@/componentes/ui/Cajon"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { tienePermiso } from "@/lib/autorizacion"
import { paraElServidor } from "@/lib/decimales"
import { usarSesion } from "@/lib/sesion"

/**
 * La ficha de una orden: sus conceptos, su desglose fiscal y lo que se puede hacer
 * con ella según su estado.
 *
 * ⚠ **Los botones que se ven salen del ESTADO y del permiso, y los dos filtran sólo
 * la vista.** El servidor rechaza la transición imposible con `TRANSICION_INVALIDA`
 * y el permiso que falta con un 404. Esconder el botón no es un permiso.
 *
 * ⚠ **El IVA y su retención no se teclean: se ENSEÑAN.** Salen de la configuración
 * fiscal y de la ficha del proveedor —100 % al contribuyente especial, 75 % al
 * ordinario—, y la pantalla dice cuál se va a aplicar y por qué. Un campo libre
 * abriría la puerta a retenerle el 75 % a un especial y descuadrar la declaración del
 * mes; lo que se corrige es la ficha, no la orden.
 *
 * ⚠ **La alícuota de ISLR y su sustraendo sí se teclean**, porque dependen de la
 * naturaleza del pago —servicios, honorarios, arrendamiento— y eso no lo sabe el
 * sistema. Llegan propuestos y se ajustan.
 */
export function FichaOrden({
  ordenId,
  onCerrar,
  desdeTesoreria = false,
}: {
  ordenId: number
  onCerrar: () => void
  /**
   * Sólo en Tesorería se paga. La misma ficha, abierta desde «Órdenes de pago», no
   * ofrece ejecutar ni revertir: el dinero sale por un solo sitio.
   */
  desdeTesoreria?: boolean
}) {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [pendientes, setPendientes] = useState<Adjuntando[]>([])
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  const [porBorrar, setPorBorrar] = useState<number | null>(null)
  const [rechazados, setRechazados] = useState<string[]>([])
  const [motivo, setMotivo] = useState("")
  // El valor es también la ruta del servidor: `accion.mutate({ ruta: pidiendo })`.
  const [pidiendo, setPidiendo] = useState<
    "rechazar" | "anular" | "revertir-pago" | "anular-aplicacion" | "devolver-a-borrador" | null
  >(null)
  const [pagando, setPagando] = useState(false)
  // El pago —ejecución o saldo de la empresa aplicado— que se va a revertir o anular.
  const [pagoElegido, setPagoElegido] = useState<number | null>(null)
  const [borrando, setBorrando] = useState(false)
  const [confirmacion, setConfirmacion] = useState("")
  /** Qué línea se está corrigiendo. Una cada vez: dos abiertas son dos borradores. */
  const [editando, setEditando] = useState<number | null>(null)
  const [editandoDatos, setEditandoDatos] = useState(false)

  const puedeEditar = tienePermiso(usuario, "orden_pago:crear")
  const puedeAprobar = tienePermiso(usuario, "orden_pago:aprobar")
  // La excepcion a «quien redacta no aprueba». Cosmetico, como todo `tienePermiso`:
  // el servidor la vuelve a comprobar y responde 409 sin ella.
  const puedeAutoaprobar = tienePermiso(usuario, "orden_pago:autoaprobar")
  const puedeAnular = tienePermiso(usuario, "orden_pago:anular")
  // Borrar NO es anular, y por eso es otro permiso: la anulada se queda con su
  // motivo y sigue diciendo quién autorizó el gasto. Sólo lo lleva `admin`, como
  // borrar un movimiento de banco, y existe para limpiar pruebas.
  const puedeEliminar = tienePermiso(usuario, "orden_pago:eliminar")
  // Deshacer una aprobación para corregir el IMPORTE: la otra excepción del
  // superadministrador. La orden pierde su firma y hará falta una nueva al reenviarla.
  const puedeDevolver = tienePermiso(usuario, "orden_pago:devolver_a_borrador")
  // Ejecutar NO es aprobar: otro permiso, y quien lo tiene debita una cuenta. Y
  // además sólo se ofrece desde Tesorería: el servidor lo permitiría desde aquí, pero
  // tener dos puertas para el mismo pago es tener dos sitios donde equivocarse.
  const puedeEjecutar = desdeTesoreria && tienePermiso(usuario, "tesoreria:ejecutar_pago")
  // Los mismos permisos que abren `GET /proveedores/{id}/datos-pago`. Cosmético: sin
  // ellos el servidor responde 403 y el bloque no se pinta.
  const puedeVerDatosPago =
    tienePermiso(usuario, "proveedor:ver") || tienePermiso(usuario, "tesoreria:ejecutar_pago")

  const consulta = useQuery({
    queryKey: ["orden-pago", ordenId],
    queryFn: () => api.get<Orden>(`/api/v1/ordenes-pago/${ordenId}`),
  })

  function refrescar() {
    clienteQuery.invalidateQueries({ queryKey: ["orden-pago", ordenId] })
    clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
  }

  const accion = useMutation({
    mutationFn: ({ ruta, cuerpo }: { ruta: string; cuerpo?: Record<string, unknown> }) =>
      api.post<Orden>(`/api/v1/ordenes-pago/${ordenId}/${ruta}`, cuerpo),
    // El aviso dice QUÉ pasó y con qué orden: cerrar el formulario no lo decía.
    meta: { exito: (o: Orden, { ruta }: { ruta: string }) => avisoDeAccion(ruta, o.codigo) },
    onSuccess: () => {
      refrescar()
      setPidiendo(null)
      setMotivo("")
    },
  })

  // ⚠ **Borra la orden de verdad, y por eso pide confirmación escribiendo el
  // código.** Anular deja el documento con su motivo; esto lo hace desaparecer con
  // su correlativo y sus firmas. Existe para limpiar pruebas, igual que borrar un
  // movimiento de banco, y el servidor sólo se lo permite al superadministrador.
  const eliminar = useMutation({
    mutationFn: () => api.delete(`/api/v1/ordenes-pago/${ordenId}`),
    meta: { exito: "Orden borrada" },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
      onCerrar()
    },
  })

  const agregarLinea = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.post<Orden>(`/api/v1/ordenes-pago/${ordenId}/lineas`, cuerpo),
    onSuccess: refrescar,
  })

  const quitarLinea = useMutation({
    mutationFn: (lineaId: number) =>
      api.delete<Orden>(`/api/v1/ordenes-pago/${ordenId}/lineas/${lineaId}`),
    onSuccess: refrescar,
  })

  const cambiarLinea = useMutation({
    mutationFn: ({ lineaId, cuerpo }: { lineaId: number; cuerpo: Record<string, unknown> }) =>
      api.patch<Orden>(`/api/v1/ordenes-pago/${ordenId}/lineas/${lineaId}`, cuerpo),
    onSuccess: () => {
      setEditando(null)
      refrescar()
    },
  })

  // Uno a uno y no en un lote: el servidor valida cada archivo (lista blanca,
  // magic bytes, 10 MB) y así el que pasa queda subido aunque otro se rechace.
  const subir = useMutation({
    mutationFn: async () => {
      for (const pendiente of pendientes) {
        const datos = new FormData()
        datos.append("archivo", pendiente.archivo)
        datos.append("tipo", "FACTURA")
        await api.post(`/api/v1/ordenes-pago/${ordenId}/documentos`, datos)
      }
    },
    onSuccess: () => {
      setPendientes([])
      refrescar()
    },
  })

  const quitarDocumento = useMutation({
    mutationFn: (documentoId: number) =>
      api.delete(`/api/v1/ordenes-pago/${ordenId}/documentos/${documentoId}`),
    onSuccess: () => {
      setPorBorrar(null)
      refrescar()
    },
  })

  function recibir(archivos: File[]) {
    const siguiente = sumar(pendientes, archivos)
    setPendientes(siguiente.adjuntos)
    setRechazados(siguiente.rechazados)
  }

  const orden = consulta.data
  const enBorrador = orden?.estado === "BORRADOR"
  const enAprobacion = orden?.estado === "EN_APROBACION"
  // La excepción para corregir el DOCUMENTO de una orden ya aprobada —una errata en
  // el número de control— sin repetir el circuito de las dos firmas. Lo que mueve el
  // importe sigue sin tocarse, y eso lo impone el servidor. Una ejecutada no, ni así:
  // sus movimientos están en el banco citando su código.
  const puedeCorregirAprobada =
    tienePermiso(usuario, "orden_pago:editar_aprobada") &&
    orden !== undefined &&
    orden.estado !== "EJECUTADA" &&
    orden.estado !== "ANULADA"
  // `Number` sólo para saber si hay algo exento, nunca para operar con el importe:
  // en el umbral cero no hay céntimo que perder.
  const hayExento = Number(orden?.monto_exento ?? 0) > 0
  const error =
    accion.error instanceof ErrorApi
      ? accion.error.message
      : agregarLinea.error instanceof ErrorApi
        ? agregarLinea.error.message
        : subir.error instanceof ErrorApi
          ? subir.error.message
          : null

  return (
    // `amplio`: esta ficha se TRABAJA —conceptos con cantidad y precio, desglose
    // fiscal y un formulario—, y en el ancho normal cada línea se parte en dos.
    <Cajon
      abierto
      titulo={orden ? orden.codigo : "Orden de pago"}
      onCerrar={onCerrar}
      ancho="amplio"
    >
      {consulta.isPending && <EstadoCarga etiqueta="Cargando la orden…" />}
      {consulta.isError && (
        <EstadoError
          titulo="No se pudo cargar la orden."
          error={consulta.error}
          onReintentar={() => consulta.refetch()}
        />
      )}
      {orden && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Insignia tono={TONO_ESTADO[orden.estado]}>
              {ESTADOS.find((e) => e.valor === orden.estado)?.etiqueta ?? orden.estado}
            </Insignia>
            {/* Las dos firmas coinciden: se salto el control de doble firma con la
                excepcion `orden_pago:autoaprobar`. Se dice, no se esconde. */}
            {orden.aprobada_por !== null && orden.aprobada_por === orden.creada_por && (
              <Insignia tono="aviso">Autoaprobada</Insignia>
            )}
            {orden.sin_documento_fiscal && <Insignia tono="neutro">Sin documento fiscal</Insignia>}
            <span className="text-sm" style={{ color: "var(--fg-muted)" }}>
              {orden.proveedor} · {orden.concepto}
            </span>
            {orden.cuenta_contable && (
              <Insignia tono="info">Cuenta {orden.cuenta_contable}</Insignia>
            )}
            {/* El proveedor y la descripción son la cabecera del documento, y hasta
                ahora sólo se podían fijar al crearlo: una errata obligaba a anular la
                orden y redactarla otra vez. Con la orden ya aprobada sólo lo ofrece
                `orden_pago:editar_aprobada`, y el servidor acota qué campos admite. */}
            {(enBorrador ? puedeEditar : puedeCorregirAprobada) && (
              <button
                type="button"
                aria-label="Editar los datos de la orden"
                onClick={() => setEditandoDatos((v) => !v)}
              >
                <Pencil size={16} />
              </button>
            )}
          </div>

          {editandoDatos && (enBorrador ? puedeEditar : puedeCorregirAprobada) && (
            <DatosOrden
              orden={orden}
              soloDocumento={!enBorrador}
              onCerrar={() => setEditandoDatos(false)}
            />
          )}

          {/* Lo escriben el rechazo y la devolución del superadministrador, y las dos
              la dejan en borrador: el rótulo dice eso, que es verdad en los dos casos. */}
          {orden.motivo_rechazo && (
            <p className="text-sm" style={{ color: "var(--destructive)" }}>
              Devuelta a borrador: {orden.motivo_rechazo}
            </p>
          )}
          {orden.motivo_anulacion && (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              Anulada: {orden.motivo_anulacion}
            </p>
          )}

          <section className="space-y-2">
            {/* ⚠ **Lo registrado y lo que se está añadiendo son dos bloques, no una
                lista seguida.** Con el formulario pegado al final de los conceptos,
                la fila vacía se lee como una línea más de la orden —y en una factura
                de ocho conceptos eso es justo lo que hace dudar de si ya se guardó. */}
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold">Conceptos registrados</h3>
              {orden.lineas.length > 0 && (
                <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                  {orden.lineas.length} {orden.lineas.length === 1 ? "concepto" : "conceptos"} ·{" "}
                  {orden.monto_base} {orden.divisa ?? ""}
                </span>
              )}
            </div>
            {orden.lineas.length === 0 && (
              <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                Sin conceptos todavía. Una orden sin conceptos no se puede enviar a aprobación.
              </p>
            )}
            <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
              {orden.lineas.map((linea) =>
                editando === linea.id ? (
                  <li key={linea.id}>
                    <EditarLinea
                      linea={linea}
                      onGuardar={(cambios) =>
                        cambiarLinea.mutate({ lineaId: linea.id, cuerpo: cambios })
                      }
                      onCerrar={() => setEditando(null)}
                      guardando={cambiarLinea.isPending}
                    />
                  </li>
                ) : (
                  <li
                    key={linea.id}
                    className="flex items-center justify-between gap-3 py-2 text-sm"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{linea.descripcion}</span>
                      {/* La insignia y las cifras, en su renglón: en una sola línea con
                          el texto, un concepto largo empujaba el monto fuera de la vista. */}
                      <span className="mt-0.5 flex items-center gap-2 text-xs">
                        {/* ⚠ Qué es cada línea se VE, y las CUATRO se distinguen entre
                            sí: sin esto, al desglosar la factura no hay forma de saber
                            cuál entra al depósito, cuál se deprecia y cuál fue un
                            servicio que se acabó al pagarlo. Ver `naturaleza.tsx`. */}
                        <InsigniaNaturaleza linea={linea} />
                        {/* ⚠ Se VE cuál no paga IVA: si no, un desglose en el que el
                            impuesto no cuadra con el 16 % de la base no tiene
                            explicación a la vista, y se acaba dudando del cálculo. */}
                        {linea.exento_iva && <Insignia tono="aviso">Exento de IVA</Insignia>}
                        {/* Su partida propia, si no es la de la orden: oficina y limpieza
                            en la misma factura van a dos partidas. */}
                        {linea.categoria_nombre && (
                          <Insignia tono="info">{linea.categoria_nombre}</Insignia>
                        )}
                        <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
                          {comoCantidad(linea.cantidad)} × {linea.precio_unitario}
                        </span>
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="tabular-nums">{linea.monto}</span>
                      {enBorrador && puedeEditar && (
                        <>
                          {/* ⚠ Se EDITA, no se quita y se vuelve a escribir. Corregir
                              un céntimo en una orden de ocho conceptos rehaciendo la
                              línea entera se hace una vez; a la segunda se deja mal. */}
                          <button
                            type="button"
                            aria-label={`Editar ${linea.descripcion}`}
                            onClick={() => setEditando(linea.id)}
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            aria-label={`Quitar ${linea.descripcion}`}
                            onClick={() => quitarLinea.mutate(linea.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </span>
                  </li>
                ),
              )}
            </ul>

            {enBorrador && puedeEditar && (
              <div
                className="mt-3 space-y-2 border-t pt-3"
                style={{ borderColor: "var(--border)" }}
              >
                <h4 className="text-xs font-semibold" style={{ color: "var(--fg-muted)" }}>
                  Añadir un concepto
                </h4>
                <NuevaLinea onAnadir={(cuerpo) => agregarLinea.mutate(cuerpo)} />
              </div>
            )}
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Desglose</h3>
            <dl className="space-y-1 text-sm">
              <Renglon etiqueta="Base" valor={`${orden.monto_base} ${orden.divisa ?? ""}`} />
              {/* ⚠ Cuando hay renglones exentos, el desglose DICE cuánto lo está: sin
                  estas dos líneas, un IVA que no es el 16 % de la base parece un error
                  de cálculo y no hay forma de comprobarlo desde la pantalla. */}
              {hayExento && (
                <>
                  <Renglon etiqueta="Exento de IVA" valor={`− ${orden.monto_exento}`} />
                  <Renglon etiqueta="Base imponible" valor={orden.monto_gravado} />
                </>
              )}
              {orden.aplica_iva && (
                <Renglon etiqueta={`IVA (${pct(orden.tasa_iva)})`} valor={orden.monto_iva} />
              )}
              {/* ⚠ No es IVA: no da crédito fiscal ni admite retención, pero está en la
                  factura y se paga. Con su nombre, para que no se lea como un IVA raro. */}
              {Number(orden.monto_otros_impuestos) > 0 && (
                <Renglon
                  etiqueta={`Otros impuestos${orden.concepto_otros_impuestos ? ` (${orden.concepto_otros_impuestos})` : ""}`}
                  valor={orden.monto_otros_impuestos}
                />
              )}
              <Renglon etiqueta="Total factura" valor={orden.monto_total} />
              {orden.aplica_ret_iva && (
                <Renglon
                  etiqueta={`Retención de IVA (${pct(orden.tasa_ret_iva)})`}
                  valor={`− ${orden.monto_ret_iva}`}
                />
              )}
              {orden.aplica_islr && (
                <Renglon
                  etiqueta={`ISLR (${pct(orden.alicuota_islr)})`}
                  valor={`− ${orden.monto_islr}`}
                />
              )}
              <Renglon
                etiqueta="A pagar al proveedor"
                valor={`${orden.monto_neto} ${orden.divisa ?? ""}`}
                fuerte
              />
            </dl>

            {enBorrador && puedeEditar && (
              <Impuestos
                orden={orden}
                aplicando={accion.isPending}
                onAplicar={(cuerpo) => accion.mutate({ ruta: "impuestos", cuerpo })}
              />
            )}
          </section>

          {/* Pendiente por ejecutar: cómo se le paga al proveedor, por clase de dato
              (03/11, pedido del usuario). El método se elige aquí; al pagar, en el modal. */}
          {orden.estado === "APROBADA" && puedeVerDatosPago && (
            <DatosParaPagar proveedorId={orden.proveedor_id} />
          )}

          <BloquePago
            orden={orden}
            onRevertir={
              puedeEjecutar
                ? (id) => {
                    setPagoElegido(id)
                    setPidiendo("revertir-pago")
                  }
                : undefined
            }
            onAnularAplicacion={
              puedeEjecutar
                ? (id) => {
                    setPagoElegido(id)
                    setPidiendo("anular-aplicacion")
                  }
                : undefined
            }
          />

          {/* El pegado se escucha en la sección entera: sube por el árbol desde
              donde esté el cursor, que es como llega de verdad una factura. */}
          <section
            className="space-y-2"
            onPaste={(evento) => {
              if (!puedeEditar || orden.estado === "ANULADA") return
              const archivos = archivosPegados(evento.clipboardData, pendientes.length)
              if (archivos.length === 0) return
              evento.preventDefault()
              recibir(archivos)
            }}
          >
            <h3 className="text-sm font-semibold">Respaldo</h3>
            <ul className="space-y-1 text-sm">
              {orden.documentos.map((documento) => {
                // `OP-2026-0036-01.png` y no `image.png`: se lee de un vistazo y, al
                // descargarlo, cae en la carpeta junto a los de su orden. Lo pone el
                // SERVIDOR con un correlativo guardado, así que borrar uno no renombra
                // los demás; el nombre con que llegó se ve al pasar el cursor.
                const nombre = documento.nombre || documento.nombre_original
                return (
                  <li key={documento.id} className="flex flex-wrap items-center gap-2">
                    {/* ⚠ Se ve AQUÍ, en un modal: el enlace sacaba de la ficha y había que
                      volver y recargar sólo para echarle un vistazo a la factura. */}
                    <button
                      type="button"
                      onClick={() =>
                        setViendo({
                          ruta: `/api/v1/ordenes-pago/${orden.id}/documentos/${documento.id}/contenido`,
                          nombre,
                        })
                      }
                      title={`Subido como «${documento.nombre_original}»`}
                      className="foco-cristal flex min-w-0 items-center gap-2 text-left underline"
                    >
                      <Paperclip size={14} className="shrink-0" />
                      <span className="truncate tabular-nums">{nombre}</span>
                      <Eye size={14} className="shrink-0" aria-hidden={true} />
                    </button>
                    {/* El comprobante de un pago no se ofrece: respalda sus movimientos en
                      el banco, y el servidor lo niega igual (409). */}
                    {puedeEditar &&
                      orden.estado !== "ANULADA" &&
                      !orden.ejecuciones.some((e) => e.documento_id === documento.id) &&
                      (porBorrar === documento.id ? (
                        <span className="flex items-center gap-2 text-xs">
                          ¿Borrarlo?
                          <Boton
                            tamano="sm"
                            variante="peligro"
                            disabled={quitarDocumento.isPending}
                            onClick={() => quitarDocumento.mutate(documento.id)}
                          >
                            Sí, borrar
                          </Boton>
                          <Boton tamano="sm" onClick={() => setPorBorrar(null)}>
                            No
                          </Boton>
                        </span>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Borrar ${nombre}`}
                          onClick={() => setPorBorrar(documento.id)}
                          className="foco-cristal toque-cristal inline-flex size-8 items-center justify-center rounded-[var(--radius-control)]"
                        >
                          <Trash2 size={14} />
                        </button>
                      ))}
                  </li>
                )
              })}
              {orden.documentos.length === 0 && (
                <li style={{ color: "var(--fg-muted)" }}>Sin documentos.</li>
              )}
            </ul>
            {quitarDocumento.isError && (
              <p className="text-xs" role="alert" style={{ color: "var(--destructive)" }}>
                {quitarDocumento.error instanceof Error
                  ? quitarDocumento.error.message
                  : "No se pudo borrar el documento."}
              </p>
            )}
            <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
            {puedeEditar && orden.estado !== "ANULADA" && (
              <>
                <AdjuntosNuevos
                  adjuntos={pendientes}
                  rechazados={rechazados}
                  onArchivos={recibir}
                  etiqueta="Adjuntar factura"
                  onQuitar={(id) => setPendientes(pendientes.filter((p) => p.id !== id))}
                />
                {pendientes.length > 0 && (
                  <div className="flex justify-end">
                    <Boton
                      tamano="sm"
                      variante="primario"
                      disabled={subir.isPending}
                      onClick={() => subir.mutate()}
                    >
                      {subir.isPending ? "Subiendo…" : `Subir ${pendientes.length}`}
                    </Boton>
                  </div>
                )}
              </>
            )}
          </section>

          {error && (
            <p className="text-sm" style={{ color: "var(--destructive)" }}>
              {error}
            </p>
          )}

          {borrando ? (
            <form
              className="space-y-2"
              onSubmit={(evento) => {
                evento.preventDefault()
                eliminar.mutate()
              }}
            >
              {/* ⚠ Se teclea el código a propósito: un «¿seguro?» se pulsa sin leerlo,
                  y esto no se deshace — no hay papelera ni anulación que lo recupere. */}
              <label className="block space-y-1" htmlFor="orden-borrar">
                <span className="text-sm font-medium">
                  Escribe <strong>{orden.codigo}</strong> para borrarla
                </span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  Esto no se deshace: desaparecen el documento, su número y sus firmas. Para
                  deshacer un gasto real, anúlala.
                </span>
                <input
                  id="orden-borrar"
                  value={confirmacion}
                  onChange={(evento) => setConfirmacion(evento.target.value)}
                  className="campo mt-1 w-full"
                />
              </label>
              {eliminar.error instanceof ErrorApi && (
                <p className="text-sm" style={{ color: "var(--destructive)" }}>
                  {eliminar.error.message}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Boton
                  onClick={() => {
                    setBorrando(false)
                    setConfirmacion("")
                  }}
                >
                  Cancelar
                </Boton>
                <Boton
                  type="submit"
                  variante="peligro"
                  disabled={confirmacion.trim() !== orden.codigo || eliminar.isPending}
                >
                  {eliminar.isPending ? "Borrando…" : "Borrar definitivamente"}
                </Boton>
              </div>
            </form>
          ) : pidiendo !== null ? (
            <form
              className="space-y-2"
              onSubmit={(evento) => {
                evento.preventDefault()
                // Revertir y anular apuntan a UN pago: la ruta lleva cuál.
                accion.mutate({
                  ruta:
                    pidiendo === "revertir-pago" && pagoElegido !== null
                      ? `revertir-pago?ejecucion_id=${pagoElegido}`
                      : pidiendo === "anular-aplicacion"
                        ? `aplicaciones-saldo/${pagoElegido}/anular`
                        : pidiendo,
                  cuerpo: { motivo },
                })
              }}
            >
              <label className="block space-y-1" htmlFor="orden-motivo">
                <span className="text-sm font-medium">
                  Motivo{" "}
                  {pidiendo === "rechazar"
                    ? "del rechazo"
                    : pidiendo === "anular"
                      ? "de la anulación"
                      : pidiendo === "devolver-a-borrador"
                        ? "para devolverla a borrador"
                        : pidiendo === "anular-aplicacion"
                          ? "para anular el saldo de la empresa aplicado"
                          : "de la reversión del pago"}
                </span>
                {pidiendo === "devolver-a-borrador" && orden.estado === "APROBADA" && (
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    Pierde la firma de aprobación: al corregirla y reenviarla, tendrá que aprobarse
                    otra vez.
                  </span>
                )}
                <textarea
                  id="orden-motivo"
                  required
                  minLength={10}
                  maxLength={300}
                  value={motivo}
                  onChange={(evento) => setMotivo(evento.target.value)}
                  className="campo mt-1 w-full"
                />
              </label>
              <div className="flex justify-end gap-2">
                <Boton onClick={() => setPidiendo(null)}>Cancelar</Boton>
                <Boton type="submit" variante="peligro">
                  Confirmar
                </Boton>
              </div>
            </form>
          ) : (
            <div className="flex flex-wrap justify-end gap-2">
              {enBorrador && puedeEditar && (
                <Boton
                  variante="primario"
                  onClick={() => accion.mutate({ ruta: "enviar" })}
                  disabled={accion.isPending}
                >
                  Enviar a aprobación
                </Boton>
              )}
              {enAprobacion && puedeAprobar && (
                <>
                  <Boton onClick={() => setPidiendo("rechazar")}>Rechazar</Boton>
                  {(orden.creada_por !== usuario?.id || puedeAutoaprobar) && (
                    <Boton
                      variante="primario"
                      onClick={() => accion.mutate({ ruta: "aprobar" })}
                      disabled={accion.isPending}
                      title={
                        orden.creada_por === usuario?.id
                          ? "La redactaste tú: quedará registrada como autoaprobada."
                          : undefined
                      }
                    >
                      {orden.creada_por === usuario?.id ? "Autoaprobar" : "Aprobar"}
                    </Boton>
                  )}
                </>
              )}
              {orden.estado === "APROBADA" && !desdeTesoreria && (
                <p className="mr-auto text-sm" style={{ color: "var(--fg-muted)" }}>
                  El pago se ejecuta desde Tesorería → Pagos por ejecutar.
                </p>
              )}
              {orden.estado === "APROBADA" && puedeEjecutar && (
                <Boton variante="primario" onClick={() => setPagando(true)}>
                  {orden.monto_pendiente && orden.monto_pendiente !== orden.monto_neto
                    ? "Pagar el resto"
                    : "Ejecutar pago"}
                </Boton>
              )}
              {/* En aprobación o aprobada. Una ejecutada tiene primero que revertir
                  su pago en Tesorería. */}
              {(enAprobacion || orden.estado === "APROBADA") && puedeDevolver && (
                <Boton
                  onClick={() => setPidiendo("devolver-a-borrador")}
                  title={
                    enAprobacion
                      ? "Para corregir sus conceptos o impuestos antes de que se apruebe."
                      : "Para corregir sus conceptos o impuestos. Pierde la firma de aprobación."
                  }
                >
                  Devolver a borrador
                </Boton>
              )}
              {/* Revertir va en cada pago del bloque «Pago»: con pagos parciales, «el
                  pago» de la orden no dice cuál. */}
              {puedeAnular && orden.estado !== "ANULADA" && orden.estado !== "EJECUTADA" && (
                <Boton variante="peligro" onClick={() => setPidiendo("anular")}>
                  Anular
                </Boton>
              )}
              {/* Una orden EJECUTADA no se ofrece: sus movimientos están en el
                  ledger citando su código. Primero se revierte el pago. */}
              {puedeEliminar && orden.estado !== "EJECUTADA" && (
                <Boton
                  variante="peligro"
                  onClick={() => setBorrando(true)}
                  title="Borra el documento entero. Para deshacer un gasto real, anula."
                >
                  <Trash2 size={16} /> Eliminar
                </Boton>
              )}
            </div>
          )}
        </div>
      )}
      {pagando && orden && <ModalEjecutarPago orden={orden} onCerrar={() => setPagando(false)} />}
    </Cajon>
  )
}

type ArticuloBreve = {
  id: number
  codigo: string
  nombre: string
  clase: string
  /** De aquí lo hereda la línea: lo exento es de la cosa comprada, no de la factura. */
  exento_iva: boolean
}

/** Qué se está comprando en esta línea. Es la pregunta que desglosa la factura. */
const NATURALEZAS = [
  { valor: "SERVICIO", etiqueta: "Servicio" },
  // ⚠ Los tornillos y la toma eléctrica de una reparación menor: se compran y se
  // instalan el mismo día. Como consumible entrarían al depósito con una existencia
  // que nadie guarda ni cuenta, y el conteo físico no cuadraría nunca.
  { valor: "MATERIAL", etiqueta: "Material de uso inmediato" },
  { valor: "CONSUMIBLE", etiqueta: "Consumible" },
  { valor: "BIEN", etiqueta: "Activo o bien" },
] as const

type Naturaleza = (typeof NATURALEZAS)[number]["valor"]

/**
 * El concepto que se añade a la orden, con lo que es.
 *
 * ⚠ **Sin esta elección no se puede desglosar una factura.** La compra de Daka trae
 * el dispensador y las sillas —bienes— y la de La Colmena, la reparación de la
 * tubería junto con el material: son una sola transferencia y tienen que caber en una
 * sola orden. La naturaleza es de la LÍNEA, no del documento.
 *
 * ⚠ **Elegir consumible o bien ata la línea a un artículo**, y es lo que hace que al
 * pagar entre al depósito con esta cantidad y este precio. Un servicio no mueve nada:
 * se acabó cuando se pagó.
 *
 * ⚠ **Y si ese renglón paga IVA o no, también es de la LÍNEA.** Una misma factura trae
 * lo gravado y lo exento; con la casilla sólo en la orden, marcar IVA le aplicaba el
 * 16 % a lo exento e inflaba el total, que es lo que se transfiere. Con artículo llega
 * heredada de su ficha —lo exento es de la cosa comprada— y se puede corregir aquí.
 */
function NuevaLinea({ onAnadir }: { onAnadir: (cuerpo: Record<string, unknown>) => void }) {
  const usuario = usarSesion((e) => e.usuario)
  const [naturaleza, setNaturaleza] = useState<Naturaleza>("SERVICIO")
  const [articuloId, setArticuloId] = useState<number | null>(null)
  const [descripcion, setDescripcion] = useState("")
  const [exento, setExento] = useState(false)
  const [creando, setCreando] = useState(false)

  const esArticulo = naturaleza === "CONSUMIBLE" || naturaleza === "BIEN"
  const puedeCrearArticulo = tienePermiso(usuario, "inventario:administrar")

  const articulos = useQuery({
    queryKey: ["articulos", naturaleza],
    queryFn: () => api.get<ArticuloBreve[]>(`/api/v1/articulos?clase=${naturaleza}`),
    enabled: esArticulo && tienePermiso(usuario, "inventario:ver"),
  })

  // El nombre del artículo elegido es la descripción de la línea: el concepto y la
  // cosa comprada son lo mismo, y tecleado dos veces acaban discrepando.
  //
  // ⚠ **El recién creado no está todavía en `articulos.data`** —la lista se refresca
  // después—, así que se guarda su nombre al crearlo. Sin ese respaldo, añadir una
  // línea justo después de dar de alta el artículo mandaría la descripción vacía y el
  // servidor la rechazaría, que es exactamente el primer uso de esta pantalla.
  const [nombreRecienCreado, setNombreRecienCreado] = useState<string | null>(null)
  const nombreDelArticulo =
    articulos.data?.find((a) => a.id === articuloId)?.nombre ?? nombreRecienCreado

  /** Al elegir un artículo, su ficha manda: lo exento es de la cosa, no de la orden. */
  function elegirArticulo(id: number | null) {
    setArticuloId(id)
    const elegido = articulos.data?.find((a) => a.id === id)
    if (elegido) setExento(elegido.exento_iva)
  }

  return (
    <form
      className="grid gap-2 sm:grid-cols-[auto_1fr_auto_auto_auto]"
      onSubmit={(evento) => {
        evento.preventDefault()
        const formulario = evento.currentTarget
        const datos = new FormData(formulario)
        onAnadir({
          // ⚠ En un consumible o un bien, el artículo ES el concepto: se elige una
          // vez y de ahí sale la descripción. Dos campos para lo mismo acaban
          // diciendo cosas distintas —«Almohadilla» en uno y «almohadillas para
          // sello» en el otro— y entonces el informe por artículo no agrupa nada.
          descripcion: esArticulo ? (nombreDelArticulo ?? "") : descripcion,
          cantidad: paraElServidor(String(datos.get("cantidad") ?? "1")),
          precio_unitario: paraElServidor(String(datos.get("precio_unitario") ?? "0")),
          articulo_id: esArticulo ? articuloId : null,
          uso_inmediato: naturaleza === "MATERIAL",
          // Explícito y no `null`: aquí ya se sabe, porque la casilla llegó heredada
          // del artículo o la marcó quien desglosa la factura.
          exento_iva: exento,
        })
        formulario.reset()
        setDescripcion("")
        setArticuloId(null)
        setExento(false)
        setCreando(false)
      }}
    >
      <select
        aria-label="Qué se compra en esta línea"
        value={naturaleza}
        onChange={(e) => {
          setNaturaleza(e.target.value as Naturaleza)
          setArticuloId(null)
          setExento(false)
          setCreando(false)
        }}
        className="campo"
      >
        {NATURALEZAS.map((n) => (
          <option key={n.valor} value={n.valor}>
            {n.etiqueta}
          </option>
        ))}
      </select>
      {/* ⚠ **Un solo campo en el mismo sitio**: un servicio se describe a mano y un
          consumible o un bien se ELIGE, porque el artículo ya es el concepto. */}
      {esArticulo ? (
        <SelectorBuscable
          id="linea-articulo"
          opciones={(articulos.data ?? []).map((a) => ({
            valor: a.id,
            etiqueta: a.nombre,
            detalle: a.codigo,
          }))}
          valor={articuloId}
          onCambiar={elegirArticulo}
          vacio={naturaleza === "BIEN" ? "Elige el bien" : "Elige el consumible"}
          placeholder="Nombre o código…"
        />
      ) : (
        <input
          required
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder={naturaleza === "MATERIAL" ? "Material: tornillos, toma…" : "Concepto"}
          aria-label="Concepto de la línea"
          className="campo"
        />
      )}
      <input
        name="cantidad"
        defaultValue="1"
        aria-label="Cantidad"
        className="campo w-20 tabular-nums"
      />
      <input
        name="precio_unitario"
        required
        inputMode="decimal"
        placeholder="Precio"
        aria-label="Precio unitario"
        className="campo w-28 tabular-nums"
      />
      <Boton type="submit" tamano="sm" disabled={esArticulo && articuloId === null}>
        Añadir
      </Boton>

      {/* ⚠ **Por renglón, no por factura.** Una compra de papelería trae el papel
          gravado y los libros exentos en el mismo documento: sin esta casilla, marcar
          IVA en la orden le aplica el 16 % a los libros e infla el total — y el total
          es lo que se transfiere. Con artículo llega marcada desde su ficha. */}
      <label className="flex items-center gap-2 text-xs sm:col-span-5">
        <input type="checkbox" checked={exento} onChange={(e) => setExento(e.target.checked)} />
        <span>
          Exento de IVA
          {esArticulo && articuloId !== null && (
            <span style={{ color: "var(--fg-muted)" }}> · viene de la ficha del artículo</span>
          )}
        </span>
      </label>

      {esArticulo && (
        <div className="space-y-2 sm:col-span-5">
          {/* ⚠ **Un botón a la vista, y no sólo el «crear» del buscable.** Aquél
              aparece al escribir un nombre que no existe, y eso no se descubre: la
              primera vez que se desglosa una factura, casi nada de lo que se compró
              está dado de alta. Sin un camino visible, se acaba eligiendo «cualquier
              cosa» con tal de seguir — y entonces el inventario miente. */}
          {puedeCrearArticulo && !creando && (
            <Boton tamano="sm" onClick={() => setCreando(true)}>
              {naturaleza === "BIEN" ? "No existe: crear bien" : "No existe: crear consumible"}
            </Boton>
          )}
          {creando && (
            <div className="tarjeta p-3">
              {/* ⚠ **El formulario COMPLETO, el mismo del inventario.** Uno reducido
                  aquí dejaría sin categoría de gasto y sin unidad todo lo que se da
                  de alta mientras se desglosa una factura, que es justo cuando se da
                  de alta de verdad. */}
              <FormularioArticulo
                articulo={null}
                clase={naturaleza}
                onCerrar={() => setCreando(false)}
                onGuardado={(creado) => {
                  setArticuloId(creado.id)
                  setNombreRecienCreado(creado.nombre)
                  // Recién creado tampoco está en `articulos.data`: lo exento se toma
                  // de lo que se acaba de guardar, o el primer renglón nacería gravado.
                  setExento(creado.exento_iva)
                  setCreando(false)
                }}
              />
            </div>
          )}
          <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
            Al pagar la orden, esta cantidad entra al inventario con este precio.
          </span>
        </div>
      )}
    </form>
  )
}

type ConfigFiscal = {
  tasa_iva: string
  tasa_ret_iva_normal: string
  tasa_ret_iva_especial: string
  tasa_islr_servicios: string
  valor_ut: string
}

type ProveedorFiscal = {
  id: number
  nombre: string
  contribuyente_especial: boolean
  alicuota_islr: string | null
}

/**
 * Qué impuestos lleva la orden, con las cifras a la vista antes de aplicarlas.
 *
 * ⚠ **La retención de IVA sigue saliendo de la ficha del proveedor**, y por eso aquí
 * se ENSEÑA en vez de teclearse: 100 % al contribuyente especial y 75 % al ordinario.
 * Depende de quién cobra, no de qué se compra; un campo libre sería la vía corta para
 * retenerle el 75 % a un especial y descuadrar la declaración del mes. Lo que se
 * corrige es la ficha, y aquí se dice dónde.
 *
 * ⚠ **La alícuota de ISLR y su sustraendo SÍ se teclean**, porque dependen de la
 * naturaleza del pago —servicios, honorarios, arrendamiento— y eso no lo sabe el
 * sistema. Llegan propuestos: los del proveedor si los tiene, si no los de la
 * configuración fiscal.
 */
function Impuestos({
  orden,
  aplicando,
  onAplicar,
}: {
  orden: Orden
  aplicando: boolean
  onAplicar: (cuerpo: Record<string, unknown>) => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const [iva, setIva] = useState(orden.aplica_iva)
  const [retIva, setRetIva] = useState(orden.aplica_ret_iva)
  const [islr, setIslr] = useState(orden.aplica_islr)
  const [alicuota, setAlicuota] = useState<string | null>(null)
  const [sustraendo, setSustraendo] = useState<string | null>(null)
  const [otros, setOtros] = useState(paraEditar(orden.monto_otros_impuestos ?? "0"))
  const [conceptoOtros, setConceptoOtros] = useState(orden.concepto_otros_impuestos ?? "")

  const config = useQuery({
    queryKey: ["config-fiscal"],
    queryFn: () => api.get<ConfigFiscal>("/api/v1/config-fiscal"),
  })
  const proveedor = useQuery({
    queryKey: ["proveedor", orden.proveedor_id],
    queryFn: () => api.get<ProveedorFiscal>(`/api/v1/proveedores/${orden.proveedor_id}`),
    enabled: tienePermiso(usuario, "proveedor:ver"),
  })

  const especial = proveedor.data?.contribuyente_especial ?? false
  const tasaRetIva = especial
    ? config.data?.tasa_ret_iva_especial
    : config.data?.tasa_ret_iva_normal
  // Lo que se propone si nadie teclea nada: la del proveedor, y si no la de servicios.
  const alicuotaSugerida = proveedor.data?.alicuota_islr ?? config.data?.tasa_islr_servicios

  return (
    <form
      className="space-y-3 text-sm"
      onSubmit={(evento) => {
        evento.preventDefault()
        onAplicar({
          aplica_iva: iva,
          aplica_ret_iva: retIva,
          aplica_islr: islr,
          // `null` es «usa la que corresponda»: el servidor cae al proveedor y luego
          // a la configuración. Mandar la sugerida tal cual la congelaría aquí.
          ...(islr && alicuota !== null ? { alicuota_islr: alicuota } : {}),
          ...(islr && sustraendo !== null ? { sustraendo_islr: sustraendo } : {}),
          // Siempre viajan: vacío es cero, que es como se quitan.
          otros_impuestos: otros.trim() === "" ? "0" : paraElServidor(otros),
          concepto_otros_impuestos: conceptoOtros.trim() || null,
        })
      }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={iva} onChange={(e) => setIva(e.target.checked)} />
          IVA {config.data && <span className="tabular-nums">({pct(config.data.tasa_iva)})</span>}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={retIva}
            disabled={!iva}
            onChange={(e) => setRetIva(e.target.checked)}
          />
          Retener IVA {tasaRetIva && <span className="tabular-nums">({pct(tasaRetIva)})</span>}
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={islr} onChange={(e) => setIslr(e.target.checked)} />
          Retener ISLR
        </label>
        <Boton type="submit" tamano="sm" disabled={aplicando}>
          {aplicando ? "Aplicando…" : "Aplicar"}
        </Boton>
      </div>

      {/* ⚠ La casilla dice «IVA» y no es la última palabra: lo que se grava sale de los
          renglones. Sin este renglón, marcar IVA con la mitad de la factura exenta
          enseña un impuesto que no cuadra con el 16 % y parece un fallo del cálculo. */}
      {iva && Number(orden.monto_exento) > 0 && (
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          <span className="tabular-nums">{orden.monto_exento}</span> de la base está en renglones
          exentos: el IVA se calcula sobre{" "}
          <span className="tabular-nums">{orden.monto_gravado}</span>. Lo exento se marca en cada
          concepto.
        </p>
      )}

      {retIva && proveedor.data && (
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {especial
            ? `${proveedor.data.nombre} es contribuyente especial: se le retiene el ${pct(tasaRetIva ?? "0")} del IVA.`
            : `${proveedor.data.nombre} es contribuyente ordinario: se le retiene el ${pct(tasaRetIva ?? "0")} del IVA.`}{" "}
          Si no es así, se corrige en su ficha del catálogo — depende de quién cobra, no de esta
          orden.
        </p>
      )}

      {/* ⚠ **Otros impuestos: los que NO son el IVA venezolano.** El sales tax de una
          compra en EE. UU. está en la factura y se paga, pero no es IVA: sin crédito
          fiscal, sin retención, fuera del libro de compras. Se teclea como IMPORTE —cada
          jurisdicción grava distinto y a veces sólo una parte— y suma al total. */}
      <div className="grid gap-2 sm:grid-cols-[auto_1fr]">
        <label className="flex items-center gap-2">
          Otros impuestos
          <input
            value={otros}
            onChange={(e) => setOtros(e.target.value)}
            inputMode="decimal"
            aria-label="Otros impuestos (no IVA)"
            className="campo w-28 tabular-nums"
          />
        </label>
        <input
          value={conceptoOtros}
          onChange={(e) => setConceptoOtros(e.target.value)}
          maxLength={120}
          placeholder="Qué impuesto es: Sales tax Texas 8,25 %"
          aria-label="Qué impuesto es"
          className="campo"
        />
      </div>

      {islr && (
        <ConceptoIslr
          proveedorId={orden.proveedor_id}
          onProponer={(a, s) => {
            setAlicuota(paraEditar(a))
            setSustraendo(paraEditar(s))
          }}
        />
      )}
      {islr && (
        <div className="grid gap-2 sm:grid-cols-[auto_auto_1fr]">
          <label className="flex items-center gap-2">
            Alícuota
            <input
              value={alicuota ?? (alicuotaSugerida ? paraEditar(alicuotaSugerida) : "")}
              onChange={(e) => setAlicuota(e.target.value)}
              inputMode="decimal"
              aria-label="Alícuota de ISLR"
              className="campo w-24 tabular-nums"
            />
          </label>
          <label className="flex items-center gap-2">
            Sustraendo
            <input
              value={sustraendo ?? paraEditar(orden.sustraendo_islr)}
              onChange={(e) => setSustraendo(e.target.value)}
              inputMode="decimal"
              aria-label="Sustraendo del ISLR"
              className="campo w-28 tabular-nums"
            />
          </label>
          <span className="self-center text-xs" style={{ color: "var(--fg-muted)" }}>
            El sustraendo se resta del ISLR calculado. Sale de la unidad tributaria
            {config.data && ` (UT ${config.data.valor_ut})`}; déjalo en cero si no aplica.
          </span>
        </div>
      )}
    </form>
  )
}

/**
 * Corrige una línea ya creada, en su sitio.
 *
 * ⚠ **Lo que NO se edita aquí es qué es**: pasar un servicio a consumible cambia lo
 * que entrará al depósito al pagar, y eso se decide al añadir la línea. Para
 * cambiarlo se quita y se vuelve a poner, que deja las dos acciones a la vista en la
 * bitácora en vez de una mutación silenciosa.
 */
function EditarLinea({
  linea,
  onGuardar,
  onCerrar,
  guardando,
}: {
  linea: Linea
  onGuardar: (cambios: Record<string, unknown>) => void
  onCerrar: () => void
  guardando: boolean
}) {
  const [descripcion, setDescripcion] = useState(linea.descripcion)
  // Sin los ceros de relleno: se edita «1», no «1.000000».
  const [cantidad, setCantidad] = useState(paraEditar(linea.cantidad))
  const [precio, setPrecio] = useState(paraEditar(linea.precio_unitario))
  const [exento, setExento] = useState(linea.exento_iva)
  // Naturaleza y categoría: las arma `ClasificarLinea` y viajan con el resto.
  const [clasificacion, setClasificacion] = useState<Record<string, unknown>>({})

  const esArticulo = linea.articulo_id !== null

  return (
    <div className="tarjeta grid gap-2 p-3 sm:grid-cols-[1fr_auto_auto_auto_auto]">
      <input
        value={descripcion}
        onChange={(e) => setDescripcion(e.target.value)}
        aria-label="Concepto"
        maxLength={250}
        // El concepto de una línea con artículo es su nombre: cambiarlo aquí haría
        // que la orden dijera una cosa y el depósito otra.
        disabled={esArticulo}
        className="campo"
      />
      <input
        value={cantidad}
        onChange={(e) => setCantidad(e.target.value)}
        aria-label="Cantidad"
        inputMode="decimal"
        className="campo w-20 tabular-nums"
      />
      <input
        value={precio}
        onChange={(e) => setPrecio(e.target.value)}
        aria-label="Precio unitario"
        inputMode="decimal"
        className="campo w-28 tabular-nums"
      />
      <Boton tamano="sm" onClick={onCerrar}>
        Cancelar
      </Boton>
      <Boton
        tamano="sm"
        variante="primario"
        disabled={guardando}
        onClick={() =>
          onGuardar({
            // Sólo lo que cambió: un `None` en el servidor significa «no lo toques».
            // Se comparan los valores ya recortados, o «1» frente a «1.000000»
            // parecería un cambio y se mandaría en cada guardado.
            ...(esArticulo || descripcion === linea.descripcion ? {} : { descripcion }),
            ...(cantidad === paraEditar(linea.cantidad)
              ? {}
              : { cantidad: paraElServidor(cantidad) }),
            ...(precio === paraEditar(linea.precio_unitario)
              ? {}
              : { precio_unitario: paraElServidor(precio) }),
            ...(exento === linea.exento_iva ? {} : { exento_iva: exento }),
            ...clasificacion,
          })
        }
      >
        {guardando ? "Guardando…" : "Guardar"}
      </Boton>
      {/* Se corrige aquí y no quitando la línea: una factura con ocho renglones, dos
          de ellos exentos, se marca mal una vez — y rehacerla entera para arreglarlo
          es exactamente lo que hace que se deje mal. */}
      <label className="flex items-center gap-2 text-xs sm:col-span-5">
        <input type="checkbox" checked={exento} onChange={(e) => setExento(e.target.checked)} />
        Exento de IVA
      </label>
      <div className="sm:col-span-5">
        <ClasificarLinea linea={linea} onCambio={setClasificacion} />
      </div>
    </div>
  )
}
/** `18.000000` es ilegible en una etiqueta; `18 %` es lo que se lee. */
function pct(valor: string): string {
  const numero = Number(valor)
  return Number.isFinite(numero) ? `${numero.toLocaleString("es-VE")} %` : valor
}

function Renglon({
  etiqueta,
  valor,
  fuerte,
}: {
  etiqueta: string
  valor: string
  fuerte?: boolean
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt style={{ color: "var(--fg-muted)" }}>{etiqueta}</dt>
      <dd className={fuerte ? "font-semibold tabular-nums" : "tabular-nums"}>{valor}</dd>
    </div>
  )
}

/** Qué decir tras cada acción sobre la orden. La ruta puede llevar la consulta. */
function avisoDeAccion(ruta: string, codigo: string): { titulo: string; texto?: string } {
  const accion = ruta.split(/[?/]/)[0]
  switch (accion) {
    case "enviar":
      return {
        titulo: "Enviada a aprobación",
        texto: `${codigo} espera la firma de quien aprueba.`,
      }
    case "aprobar":
      return { titulo: "Orden aprobada", texto: `${codigo} pasó a Pagos por ejecutar.` }
    case "rechazar":
      return {
        titulo: "Orden rechazada",
        texto: `${codigo} vuelve a quien la redactó, con su motivo.`,
      }
    case "anular":
      return { titulo: "Orden anulada", texto: codigo }
    case "revertir-pago":
      return { titulo: "Pago revertido", texto: `Los movimientos de ${codigo} quedaron anulados.` }
    case "aplicaciones-saldo":
      return { titulo: "Aplicación de saldo anulada", texto: codigo }
    case "devolver-a-borrador":
      return { titulo: "Devuelta a borrador", texto: codigo }
    case "impuestos":
      return { titulo: "Impuestos aplicados", texto: codigo }
    default:
      return { titulo: "Hecho", texto: codigo }
  }
}
